import React, { useState, useEffect, useCallback, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { userAccessService } from 'api/services';
import type { AccessOptionsDto, UserAccessDetailDto, UserEntityAccessDto } from 'api/types';
import { SettingsModalFrame, DetailList } from './SettingsModalFrame';

// ============================================================================
// USER ACCESS MODAL (Settings → User Access): one person in tabs
//   Roles            the roles they hold
//   Where they work  the whole company, or chosen entities with all or chosen
//                    business units and all or chosen financial years
//   Sign-in          who they are in the identity system (read-only)
// Modes: view (read-only, Edit switches to edit) and edit. Minimizes to the bar
// with its unsaved state. The editor can give only what they have themselves
// (the server checks it too); only a Super Administrator gives or takes that role.
// ============================================================================

export type UserAccessModalMode = 'view' | 'edit';
type AccessTab = 'roles' | 'where' | 'signin';

interface Draft { roleIds: string[]; isTenantWide: boolean; entities: UserEntityAccessDto[] }

const toDraft = (d: UserAccessDetailDto): Draft => ({
  roleIds: [...d.roleIds].sort(),
  isTenantWide: d.isTenantWide,
  entities: d.entities.map(e => ({ ...e, businessUnitIds: [...e.businessUnitIds].sort(), financialYearIds: [...e.financialYearIds].sort() }))
    .sort((a, b) => a.entityId.localeCompare(b.entityId)),
});
const same = (a: Draft, b: Draft) => JSON.stringify(a) === JSON.stringify(b);
const toggleIn = (list: string[], id: string, on: boolean) => (on ? [...new Set([...list, id])] : list.filter(x => x !== id)).sort();
const fmtWhen = (s?: string | null) => (s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'never');

const box: React.CSSProperties = { border: '1px solid var(--border-color)', borderRadius: '0.5rem', padding: '0.7rem 0.9rem' };
const chip: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.84rem', marginRight: '1rem', marginBottom: '0.35rem', cursor: 'pointer' };
const note: React.CSSProperties = { fontSize: '0.76rem', color: 'var(--text-secondary)' };

export interface UserAccessModalProps {
  isOpen: boolean;
  mode: UserAccessModalMode;
  userId: string | null;
  options: AccessOptionsDto | null;
  canEdit: boolean;
  initialTab?: AccessTab;
  onEdit: (userId: string) => void;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName: string;
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
  onRestoreModal?: (mode: UserAccessModalMode, userId: string) => void;
}

const UserAccessModal: React.FC<UserAccessModalProps> = ({
  isOpen, mode, userId, options, canEdit, initialTab, onEdit, onSaved, onClose, tabId, moduleName, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const [detail, setDetail] = useState<UserAccessDetailDto | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [tab, setTab] = useState<AccessTab>('roles');
  const [addEntityId, setAddEntityId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const isRestoreRef = useRef(false);
  const stateRef = useRef({ detail, draft, tab, mode, userId });
  stateRef.current = { detail, draft, tab, mode, userId };

  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    if (!userId) return;
    setTab(initialTab ?? 'roles'); setAddEntityId('');
    let cancelled = false;
    setDetail(null); setDraft(null);
    setLoading(true);
    userAccessService.getUser(userId).then(d => {
      if (cancelled) return;
      setDetail(d); setDraft(toDraft(d));
    }).catch(err => {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the user.');
      onClose();
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  // Switching view → edit keeps what is loaded.
  useEffect(() => { if (isOpen && mode === 'edit' && detail && !draft) setDraft(toDraft(detail)); }, [isOpen, mode, detail, draft]);

  const editable = canEdit && mode === 'edit';
  const dirty = !!(editable && detail && draft && !same(toDraft(detail), draft));

  const upd = (patch: Partial<Draft>) => setDraft(d => (d ? { ...d, ...patch } : d));
  const updEntity = (entityId: string, patch: Partial<UserEntityAccessDto>) =>
    setDraft(d => (d ? { ...d, entities: d.entities.map(e => (e.entityId === entityId ? { ...e, ...patch } : e)) } : d));

  const addEntity = () => {
    const opt = options?.entities.find(e => e.id === addEntityId);
    if (!opt || !draft) return;
    const all = opt.canGrantAllBusinessUnits;
    upd({
      entities: [...draft.entities, {
        entityId: opt.id, allBusinessUnits: all, businessUnitIds: all ? [] : opt.businessUnits.map(b => b.id).sort(),
        allFinancialYears: true, financialYearIds: [],
      }].sort((a, b) => a.entityId.localeCompare(b.entityId)),
    });
    setAddEntityId('');
  };

  const handleClose = useCallback(() => { if (dirty) setDiscardOpen(true); else onClose(); }, [dirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const s = stateRef.current;
    if (!s.userId) return;
    const who = s.detail?.user.userName || s.detail?.user.fullName || 'user';
    const saved = { ...s };
    minimize({
      id: `${tabId}-user-access-modal-${s.userId}`,
      title: `${s.mode === 'edit' ? 'Edit access' : 'Access'} - ${who}`,
      moduleName, tabId,
      isDirty: !!(s.mode === 'edit' && s.detail && s.draft && !same(toDraft(s.detail), s.draft)),
      onRestore: () => {
        isRestoreRef.current = true;
        setDetail(saved.detail); setDraft(saved.draft); setTab(saved.tab);
        onRestoreModal?.(saved.mode, saved.userId!);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleConfirmSave = async () => {
    if (!detail || !draft) return;
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const d = await userAccessService.save(detail.user.userId, { ...draft, accessStamp: detail.accessStamp });
      notificationService.toast.success(d.user.waitingForAccess
        ? 'Saved. This user has no role or no access yet, so they will see nothing.'
        : 'Access saved. It applies within a few seconds.');
      setSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the access.');
    }
  };

  if (!isOpen) return null;

  const u = detail?.user;
  const entityName = (id: string) => options?.entities.find(e => e.id === id)?.name ?? 'An entity outside your access';
  const freeEntities = (options?.entities ?? []).filter(e => !draft?.entities.some(x => x.entityId === e.id));
  const roleName = (id: string) => options?.roles.find(r => r.id === id)?.name ?? u?.roles.find(r => r.id === id)?.name ?? id;

  return (
    <>
      <SettingsModalFrame<AccessTab>
        title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          {mode === 'edit' ? 'Edit access' : 'Access'} — {u?.fullName || u?.userName || ''}
          {u?.waitingForAccess && <span className="badge" style={{ background: '#d97706', color: '#fff', fontSize: '0.7rem' }}>Waiting</span>}
        </span>}
        subtitle={u ? `${[u.userName, u.email].filter(Boolean).join(' · ')} · last sign-in ${fmtWhen(u.lastLoginAt)}` : undefined}
        onClose={handleClose}
        onMinimize={tabId ? handleMinimize : undefined}
        busy={saving || loading}
        busyLabel={loading ? 'Loading…' : 'Saving…'}
        tabs={[
          { id: 'roles', label: 'Roles', count: draft?.roleIds.length ?? 0 },
          { id: 'where', label: 'Where they work' },
          { id: 'signin', label: 'Sign-in' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
        footer={<>
          <span style={{ flex: 1, ...note }}>
            {mode === 'view' ? ''
              : dirty ? 'Not saved yet.'
              : draft && (draft.roleIds.length === 0 || (!draft.isTenantWide && draft.entities.length === 0)) ? 'Without a role and somewhere to work, this person sees nothing.' : ''}
          </span>
          {mode === 'view' ? (
            <>
              {canEdit && u && <button className="btn btn-secondary" onClick={() => onEdit(u.userId)}><IconSystem.edit size={14} /> Edit</button>}
              <button className="btn btn-primary" onClick={onClose}>Close</button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" style={{ color: '#dc2626', marginRight: 'auto' }} disabled={saving}
                onClick={() => upd({ roleIds: [], isTenantWide: false, entities: [] })}
                title="Remove every role and all access (save to apply)">No access</button>
              <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={() => setSaveConfirmOpen(true)} disabled={saving || !dirty}>Save</button>
            </>
          )}
        </>}
      >
        {draft && options && tab === 'roles' && (
          <div style={box}>
            {mode === 'view' ? (
              draft.roleIds.length === 0 ? <div style={note}>No role: this person signs in but sees nothing.</div>
                : draft.roleIds.map(id => <div key={id} style={{ padding: '0.25rem 0', fontSize: '0.875rem' }}>{roleName(id)}</div>)
            ) : options.roles.map(r => (
              <label key={r.id} style={chip}>
                <input type="checkbox" checked={draft.roleIds.includes(r.id)} disabled={!editable}
                  onChange={e => upd({ roleIds: toggleIn(draft.roleIds, r.id, e.target.checked) })} />
                {r.name}
              </label>
            ))}
            <div style={{ ...note, marginTop: '0.4rem' }}>What each role may do is set under Roles &amp; Permissions. Only a Super Administrator can give or take that role.</div>
          </div>
        )}

        {draft && options && tab === 'where' && (
          <div style={{ display: 'grid', gap: '0.7rem', alignContent: 'start' }}>
            <div style={box}>
              <label style={chip}>
                <input type="radio" checked={draft.isTenantWide} disabled={!editable || !options.canGrantTenantWide} onChange={() => upd({ isTenantWide: true })} />
                The whole company (every entity, business unit and year)
              </label>
              <label style={chip}>
                <input type="radio" checked={!draft.isTenantWide} disabled={!editable} onChange={() => upd({ isTenantWide: false })} />
                Chosen entities
              </label>
              {editable && !options.canGrantTenantWide && <div style={note}>Only someone with access to the whole company can give it.</div>}
            </div>

            {!draft.isTenantWide && (
              <>
                {draft.entities.length === 0 && <div style={note}>No entity yet{editable ? ': add one below.' : '.'}</div>}
                {draft.entities.map(e => {
                  const opt = options.entities.find(o => o.id === e.entityId);
                  return (
                    <div key={e.entityId} style={box}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                        <strong style={{ fontSize: '0.88rem' }}>{entityName(e.entityId)}</strong>
                        {editable && (
                          <button className="btn btn-secondary" style={{ fontSize: '0.74rem', padding: '0.15rem 0.5rem' }}
                            onClick={() => upd({ entities: draft.entities.filter(x => x.entityId !== e.entityId) })}>
                            <IconSystem.trash size={12} /> Remove
                          </button>
                        )}
                      </div>
                      {!opt ? <div style={note}>You cannot change access to an entity outside your own; remove it or leave it as it is.</div> : (
                        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: '0.3rem', fontSize: '0.84rem' }}>
                          <div style={{ color: 'var(--text-secondary)' }}>Business units</div>
                          <div>
                            <label style={chip}>
                              <input type="checkbox" checked={e.allBusinessUnits} disabled={!editable || !opt.canGrantAllBusinessUnits}
                                onChange={ev => updEntity(e.entityId, { allBusinessUnits: ev.target.checked, businessUnitIds: ev.target.checked ? [] : opt.businessUnits.map(b => b.id).sort() })} />
                              All
                            </label>
                            {!e.allBusinessUnits && opt.businessUnits.map(b => (
                              <label key={b.id} style={chip}>
                                <input type="checkbox" checked={e.businessUnitIds.includes(b.id)} disabled={!editable}
                                  onChange={ev => updEntity(e.entityId, { businessUnitIds: toggleIn(e.businessUnitIds, b.id, ev.target.checked) })} />
                                {b.name}
                              </label>
                            ))}
                          </div>
                          <div style={{ color: 'var(--text-secondary)' }}>Financial years</div>
                          <div>
                            <label style={chip}>
                              <input type="checkbox" checked={e.allFinancialYears} disabled={!editable}
                                onChange={ev => updEntity(e.entityId, { allFinancialYears: ev.target.checked, financialYearIds: ev.target.checked ? [] : opt.financialYears.filter(f => f.isCurrent).map(f => f.id) })} />
                              All
                            </label>
                            {!e.allFinancialYears && opt.financialYears.map(f => (
                              <label key={f.id} style={chip}>
                                <input type="checkbox" checked={e.financialYearIds.includes(f.id)} disabled={!editable}
                                  onChange={ev => updEntity(e.entityId, { financialYearIds: toggleIn(e.financialYearIds, f.id, ev.target.checked) })} />
                                {f.name}{f.isCurrent ? ' (current)' : ''}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {editable && freeEntities.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <select className="form-input" style={{ fontSize: '0.84rem', maxWidth: 340 }} value={addEntityId} onChange={e => setAddEntityId(e.target.value)}>
                      <option value="">Add an entity…</option>
                      {freeEntities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                    <button className="btn btn-secondary" disabled={!addEntityId} onClick={addEntity}><IconSystem.add size={14} /> Add</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {u && tab === 'signin' && (
          <>
            <DetailList rows={[
              ['Name', u.fullName || '—'],
              ['User name', u.userName || '—'],
              ['Email', u.email || '—'],
              ['First seen', fmtWhen(u.firstSeenAt)],
              ['Last sign-in', fmtWhen(u.lastLoginAt)],
              ['Status', u.waitingForAccess ? 'Waiting for access' : 'Has access'],
            ]} />
            <div style={note}>Sign-in, password and disabling the login are managed in the identity system (Tenant Hub), not here.</div>
          </>
        )}
      </SettingsModalFrame>

      <ConfirmDialog open={saveConfirmOpen} title="Confirm Save"
        message={draft && draft.roleIds.length === 0 && !draft.isTenantWide && draft.entities.length === 0
          ? `Remove every role and all access from ${u?.fullName || u?.userName}? They will still sign in but see nothing.`
          : `Save the access of ${u?.fullName || u?.userName}? It applies within a few seconds.`}
        confirmLabel="Confirm" cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmSave} onCancel={() => setSaveConfirmOpen(false)} />
      <ConfirmDialog open={discardOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }} onCancel={() => setDiscardOpen(false)} />
    </>
  );
};

export default UserAccessModal;
